import React, {useState} from 'react';

import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {useCurrentUserState} from 'app/current-user/api';
import {DocSidebarPagePanels} from 'app/general/structures/DocSidebarPagePanels';
import {PaginatedList} from 'app/general/widgets/PaginatedList';
import {sweetAPI} from 'app/store/sweet-api';
import {useModelChangeSubscription} from 'app/subscriptions';
import {useTaskTemplates} from 'app/tasks/api';
import {TaskTemplateItem} from 'app/tasks/components/TaskTemplateItem';
import {SIDEBAR_PAGE_SIZE} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {TASKS_RTK_TAGS} from 'app/utils/rtk-tags';

import {DetailPageRelatedTasksSidebarFooter} from './DetailPageRelatedTasksSidebarFooter';

import type {TaskTemplateFetchType} from 'app/tasks/api';
import type {
  ListResponseType,
  TaskAssignableContentTypes,
  TaskTemplateSerializedType,
} from 'sweet-types';

interface Props {
  contentType: TaskAssignableContentTypes;
  objectId: number;
  html_url: string;
  toggleSidebar: () => unknown;
  onChange?: () => unknown;
  canAssign: boolean;
}

export function DetailPageRelatedTasksSidebar({
  contentType,
  objectId,
  html_url,
  toggleSidebar,
  onChange,
  canAssign,
}: Props): JSX.Element {
  const {timezone} = useCurrentUserState().data!;

  const actions = useAsyncActions({
    invalidateTags: sweetAPI.util.invalidateTags,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useModelChangeSubscription(
    (message, {runWhenAllRelatedMatch}) => {
      runWhenAllRelatedMatch(
        message,
        'tasktemplate',
        {
          content_type: contentType,
          id: objectId,
        },
        () => {
          // @ts-expect-error we inject tags on the API after created ts doesn't know them.
          actions.invalidateTags([{type: TASKS_RTK_TAGS.TEMPLATE, id: 'list'}]);
          onChange && onChange();
        }
      );
    },
    [contentType, objectId]
  );

  function renderTasks(data: ListResponseType<TaskTemplateSerializedType>) {
    return data?.count === 0 ? (
      <NoResultsMessage
        icon="fa-tasks"
        head={gt('No related tasks')}
        onSidebar={true}
      >
        {gt('Use the button below to assign this procedure as task')}
      </NoResultsMessage>
    ) : (
      <ul className="list-group">
        {data &&
          data.results.map((task) => (
            <li key={task.url} className="list-group-item p-0">
              <TaskTemplateItem task={task} userTimezone={timezone} />
            </li>
          ))}
      </ul>
    );
  }

  return (
    <DocSidebarPagePanels
      title={gt('Related Tasks')}
      onClose={toggleSidebar}
      loading={isLoading}
      footer={
        <DetailPageRelatedTasksSidebarFooter
          contentType={contentType}
          objectId={objectId}
          html_url={html_url}
          canAssign={canAssign}
          onAssign={() => onChange && onChange()}
        />
      }
    >
      <PaginatedList<TaskTemplateFetchType, TaskTemplateSerializedType>
        hook={useTaskTemplates}
        params={{
          object_id: objectId,
          content_type: contentType,
          page_size: SIDEBAR_PAGE_SIZE,
        }}
        render={renderTasks}
        onIsLoading={setIsLoading}
      />
    </DocSidebarPagePanels>
  );
}
